import { useCallback, useEffect, useRef, useState } from "react";
import qapi from "views_shared/app/javascript/api";
import qlog from "views_shared/app/javascript/scripts/qlog";

const CHUNK_SIZE = 100;

// Price and description for Represents product cards, read only once the Products tab is shown.
function useProductCardDetails(productIds) {
  const [shown, setShown] = useState(false);
  const [details, setDetails] = useState({});
  const requestedIds = useRef(new Set());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const onShow = useCallback(() => setShown(true), []);
  const idsKey = productIds.join(",");

  useEffect(() => {
    if (!shown || !idsKey) return;

    const missing = idsKey.split(",").filter((id) => !requestedIds.current.has(id));
    if (!missing.length) return;
    missing.forEach((id) => requestedIds.current.add(id));

    const chunks = [];
    for (let i = 0; i < missing.length; i += CHUNK_SIZE) chunks.push(missing.slice(i, i + CHUNK_SIZE));

    const load = async () => {
      try {
        const responses = await Promise.all(
          chunks.map((chunk) => qapi.products.getAllById(chunk, { select: "id,price,description" })),
        );
        if (!mounted.current) return;
        const byId = {};
        responses.forEach((response) => {
          if (response?.error) {
            qlog.error("Product card details lookup failed:", response.error);
            return;
          }
          (response?.data || []).forEach((row) => {
            byId[row.id] = { price: row.price, description: row.description };
          });
        });
        setDetails((current) => ({ ...current, ...byId }));
      } catch (error) {
        qlog.error("Product card details lookup failed:", error);
      }
    };
    load();
  }, [shown, idsKey]);

  return { details, onShow };
}

export default useProductCardDetails;
